import React, { useEffect } from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { MissionStopsProps } from "../types";
import { MissionStopDetails } from "./MissionStopDetails";
import {
  AddActionStepButton,
  AddStationStepButton,
  AddStepButtonIcon,
  AddStepButtonLabel,
  AddStepButtonsContainer,
  AddStepButtonText,
  MissionStepsEmptyState,
  MissionStepsHeader,
  MissionStepsTitle,
  StopsScrollContainer,
} from "../styles";
import { ReactComponent as AddIconSvg } from "../assets/add.svg";
import { CONFIRMATION_TYPES } from "Components/common/constants";
import { countTotalTriggerTypesForStop } from "../utils";
import {
  getValidationErrorForNewStep,
  isAddActionButtonDisabled,
  getAddActionButtonTitle,
} from "../utils/validation";
import { areActionsComplete, hasIncompleteDurationActions } from "../utils";
import { toast } from "react-toastify";

export const MissionStops: React.FC<MissionStopsProps> = ({
  missionStops,
  stops,
  availableActions,
  availableTriggerTypes,
  onAddStep,
  onAddAction,
  onStationSelect,
  onActionSelect,
  onTriggerTypeSelect,
  onDurationChange,
  onRemoveAction,
  onRemoveStep,
  onUnhitchChange,
  onReorderSteps,
  onReorderActions,
  onValidationError,
  onClearValidationError,
  listBottomRef,
}) => {
  useEffect(() => {
    if (!onClearValidationError) return;

    const validationError = getValidationErrorForNewStep(missionStops);

    if (!validationError) {
      onClearValidationError(
        "Please select a station for the current step before adding a new one"
      );
      onClearValidationError(
        "Please select a trigger type for all actions in the current step before adding a new one"
      );
    }

    // Clear duration validation error if duration is now valid
    if (missionStops.length > 0) {
      const currentStep = missionStops[missionStops.length - 1];
      if (!hasIncompleteDurationActions(currentStep)) {
        onClearValidationError(
          "Please set a valid duration (greater than 0) for all duration actions before adding a new station"
        );
      }
    }
  }, [missionStops, onClearValidationError]);

  const handleAddStep = () => {
    const validationError = getValidationErrorForNewStep(missionStops);

    if (validationError) {
      if (onValidationError) {
        onValidationError(validationError);
      }
      return;
    }

    // Check if the current step (last step) has incomplete duration actions
    if (missionStops.length > 0) {
      const currentStep = missionStops[missionStops.length - 1];
      if (hasIncompleteDurationActions(currentStep)) {
        if (onValidationError) {
          onValidationError(
            "Please set a valid duration (greater than 0) for all duration actions before adding a new station"
          );
        }
        return;
      }
    }

    onAddStep();
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId, type } = result;

    // Reorder steps
    if (type === "STEP" && draggableId.startsWith("step-")) {
      if (onReorderSteps && source.index !== destination.index) {
        onReorderSteps(source.index, destination.index);
      }
      return;
    }

    // Reorder / move actions between stops
    if (type === "ACTION" && draggableId.startsWith("action-")) {
      if (!onReorderActions) return;

      const parseStopIndex = (droppableId: string) => {
        // droppableId pattern: actions-{stepIndex}
        if (!droppableId.startsWith("actions-")) return -1;
        const raw = droppableId.replace("actions-", "");
        const idx = parseInt(raw, 10);
        return isNaN(idx) ? -1 : idx;
      };

      const startStopIndex = parseStopIndex(source.droppableId);
      const endStopIndex = parseStopIndex(destination.droppableId);

      // Validate
      if (
        startStopIndex < 0 ||
        startStopIndex >= missionStops.length ||
        endStopIndex < 0 ||
        endStopIndex >= missionStops.length
      )
        return;

      // No positional change
      if (startStopIndex === endStopIndex && source.index === destination.index)
        return;

      // Business validation: prevent moving action into a stop with double confirmation
      const targetStop = missionStops[endStopIndex];
      if (
        targetStop?.confirmationType ===
        CONFIRMATION_TYPES.CLOUD_HMI_RELEASE_CONFIRMATION
      ) {
        toast.warn(
          "Cannot move action into a Double Confirmation stop. Remove double confirmation first."
        );
        return;
      }

      // Prevent exceeding 3 total trigger types at target
      const targetTotalTriggers = countTotalTriggerTypesForStop(targetStop);
      const movingStop = missionStops[startStopIndex];
      const movingAction = movingStop?.missionStopVdaActions?.[source.index];
      const movingActionAddsTrigger =
        typeof movingAction?.actionParameters?.[0]?.triggerType === "string" &&
        movingAction.actionParameters[0].triggerType.trim().length > 0;
      if (movingActionAddsTrigger && targetTotalTriggers >= 3) {
        toast.warn(
          "Cannot move action: target stop already has maximum trigger types (3)."
        );
        return;
      }

      onReorderActions(
        startStopIndex,
        endStopIndex,
        source.index,
        destination.index
      );
    }
  };

  return (
    <MissionStepsEmptyState>
      <MissionStepsHeader>
        <MissionStepsTitle>Mission Steps</MissionStepsTitle>
        <AddStepButtonsContainer>
          <AddStationStepButton onClick={handleAddStep}>
            <AddStepButtonIcon>
              <AddIconSvg />
            </AddStepButtonIcon>
            <AddStepButtonText>
              <AddStepButtonLabel>Station</AddStepButtonLabel>
            </AddStepButtonText>
          </AddStationStepButton>
          <AddActionStepButton
            onClick={() => {
              // Add action to the last step if it exists and is enabled
              if (missionStops.length > 0) {
                const lastStepIndex = missionStops.length - 1;

                // Check if the button should be disabled (same validation as disabled prop)
                if (
                  isAddActionButtonDisabled(
                    missionStops,
                    availableActions,
                    countTotalTriggerTypesForStop,
                    CONFIRMATION_TYPES
                  )
                ) {
                  return;
                }

                onAddAction(lastStepIndex);
              }
            }}
            disabled={isAddActionButtonDisabled(
              missionStops,
              availableActions,
              countTotalTriggerTypesForStop,
              CONFIRMATION_TYPES
            )}
            title={getAddActionButtonTitle(
              missionStops,
              availableActions,
              countTotalTriggerTypesForStop,
              CONFIRMATION_TYPES
            )}
          >
            <AddStepButtonIcon>
              <AddIconSvg />
            </AddStepButtonIcon>
            <AddStepButtonText>
              <AddStepButtonLabel>Action</AddStepButtonLabel>
            </AddStepButtonText>
          </AddActionStepButton>
        </AddStepButtonsContainer>
      </MissionStepsHeader>

      <StopsScrollContainer>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="mission-steps" type="STEP">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ width: "100%" }}
              >
                {missionStops.map((stop, stopIndex) => {
                  // All steps are enabled in both create and edit mode
                  const isStepEnabled = true;

                  // Disable drag and drop when station is not defined or when actions are incomplete
                  const isDragDisabled =
                    !stop.stopPk ||
                    stop.stopPk.trim() === "" ||
                    !areActionsComplete(stop);

                  return (
                    <MissionStopDetails
                      key={stopIndex}
                      stepIndex={stopIndex}
                      missionStop={stop}
                      stops={stops}
                      availableActions={availableActions}
                      availableTriggerTypes={availableTriggerTypes}
                      onStationSelect={onStationSelect}
                      onActionSelect={onActionSelect}
                      onTriggerTypeSelect={onTriggerTypeSelect}
                      onDurationChange={onDurationChange}
                      onRemoveAction={onRemoveAction}
                      onRemoveStep={onRemoveStep}
                      onUnhitchChange={onUnhitchChange}
                      onAddAction={onAddAction}
                      onReorderActions={onReorderActions}
                      isStepEnabled={isStepEnabled}
                      isDragDisabled={isDragDisabled}
                    />
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {missionStops.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#666",
              fontSize: "14px",
            }}
          >
            Click &quot;Station&quot; to add your first mission step
          </div>
        )}

        <div ref={listBottomRef} style={{ height: 0 }} />
      </StopsScrollContainer>
    </MissionStepsEmptyState>
  );
};
